/**
 * Transcript Segmentation Utility
 * Breaks up transcripts into segments of no more than 35 chars per line and no more than 2 lines.
 * Finds boundaries at punctuation marks and converts to the format to show in columns.
 */

/**
 * Language-specific sentence boundary markers
 * Different languages may have different punctuation patterns
 */
const SENTENCE_BOUNDARIES = {
    // Common boundaries for most languages
    default: ['.', '!', '?', ';', ':', '।', '।।'], // Added Devanagari danda
    
    // Language-specific boundaries
    English: ['.', '!', '?', ';', ':'],
    Hindi: ['.', '!', '?', ';', ':', '।', '।।'],
    Gujarati: ['.', '!', '?', ';', ':', '।', '।।'],
    Sanskrit: ['.', '!', '?', ';', ':', '।', '।।'],
    Marathi: ['.', '!', '?', ';', ':', '।', '।।'],
    Bengali: ['.', '!', '?', ';', ':', '।', '।।'],
    Tamil: ['.', '!', '?', ';', ':', '।'],
    Telugu: ['.', '!', '?', ';', ':', '।'],
    Malayalam: ['.', '!', '?', ';', ':', '।'],
    Kannada: ['.', '!', '?', ';', ':', '।'],
    Chinese: ['。', '！', '？', ';', ':', '.', '!', '?'],
    Japanese: ['。', '！', '？', ';', ':', '.', '!', '?'],
    Korean: ['.', '!', '?', ';', ':', '。'],
    Arabic: ['.', '!', '؟', ';', ':', '؛'],
    Persian: ['.', '!', '؟', ';', ':', '؛'],
    Urdu: ['.', '!', '؟', ';', ':', '؛', '।'],
    French: ['.', '!', '?', ';', ':'],
    Spanish: ['.', '!', '?', ';', ':'],
    Portuguese: ['.', '!', '?', ';', ':'],
    German: ['.', '!', '?', ';', ':'],
    Italian: ['.', '!', '?', ';', ':'],
    Russian: ['.', '!', '?', ';', ':'],
    Thai: ['.', '!', '?', '๚', '๛'],
    Vietnamese: ['.', '!', '?', ';', ':'],
};

/**
 * Clause boundary markers for shorter breaks
 */
const CLAUSE_BOUNDARIES = [',', '-', '–', '—', '(', ')'];

/**
 * Word boundary characters for different scripts
 */
const WORD_BOUNDARIES = {
    // Latin-based scripts
    default: [' ', '\t', '\n'],
    // Asian scripts might not use spaces
    Chinese: ['，', '、', ' ', '\t', '\n'],
    Japanese: ['、', '，', ' ', '\t', '\n'],
    Thai: [' ', '\t', '\n'], // Thai uses spaces
    // Add more as needed
};

/**
 * Get language-specific sentence boundaries
 * @param {string} language - The language name
 * @returns {Array} Array of sentence boundary characters
 */
function getSentenceBoundaries(language) {
    return SENTENCE_BOUNDARIES[language] || SENTENCE_BOUNDARIES.default;
}

/**
 * Get language-specific word boundaries
 * @param {string} language - The language name
 * @returns {Array} Array of word boundary characters
 */
function getWordBoundaries(language) {
    return WORD_BOUNDARIES[language] || WORD_BOUNDARIES.default;
}

/**
 * Clean and normalize text
 * @param {string} text - The text to clean
 * @returns {string} Cleaned text
 */
function cleanText(text) {
    if (!text || typeof text !== 'string') return '';
    
    // Remove extra whitespace and normalize
    return text
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .replace(/\r\n/g, '\n') // Normalize line endings
        .replace(/\r/g, '\n')
        .trim();
}

/**
 * Find the best breaking point near a target position
 * @param {string} text - The text to break
 * @param {number} targetPos - Target position to break near
 * @param {string} language - Language for boundary detection
 * @param {number} maxPos - Maximum allowed position
 * @returns {number} Best breaking position
 */
function findBreakPoint(text, targetPos, language, maxPos) {
    const sentenceBoundaries = getSentenceBoundaries(language);
    const wordBoundaries = getWordBoundaries(language);
    
    // Don't break if we're already within limits
    if (targetPos >= text.length) return text.length;
    
    let bestBreak = targetPos;
    let bestScore = -1;
    
    // Search for the best breaking point within a reasonable range
    const searchStart = Math.max(0, targetPos - 10);
    const searchEnd = Math.min(maxPos, targetPos + 10);
    
    for (let i = searchStart; i <= searchEnd; i++) {
        const char = text[i];
        const nextChar = text[i + 1] || '';
        let score = 0;
        
        // Prefer sentence boundaries
        if (sentenceBoundaries.includes(char)) {
            score = 100;
            // Even better if followed by space
            if (wordBoundaries.includes(nextChar)) {
                score = 150;
            }
        }
        // Then clause boundaries
        else if (CLAUSE_BOUNDARIES.includes(char)) {
            score = 75;
            if (wordBoundaries.includes(nextChar)) {
                score = 100;
            }
        }
        // Then word boundaries
        else if (wordBoundaries.includes(char)) {
            score = 50;
        }
        // Prefer positions closer to target
        score -= Math.abs(i - targetPos) * 2;
        
        if (score > bestScore) {
            bestScore = score;
            bestBreak = i;
        }
    }
    
    // If no good break found, use target position
    if (bestScore < 0) {
        return Math.min(targetPos, maxPos);
    }
    
    // Move past the breaking character if it's punctuation
    const char = text[bestBreak];
    if (sentenceBoundaries.includes(char) || CLAUSE_BOUNDARIES.includes(char)) {
        bestBreak = Math.min(bestBreak + 1, text.length);
    }
    
    return bestBreak;
}

/**
 * Segment text into subtitle-friendly chunks
 * @param {string} text - The text to segment
 * @param {string} language - Language for boundary detection
 * @param {number} maxCharsPerLine - Maximum characters per line (default 35)
 * @param {number} maxLines - Maximum lines per segment (default 2)
 * @returns {Array} Array of text segments
 */
export function segmentTranscript(text, language = 'English', maxCharsPerLine = 35, maxLines = 2) {
    const cleanedText = cleanText(text);
    if (!cleanedText) return [];
    
    const segments = [];
    let currentPos = 0;
    
    while (currentPos < cleanedText.length) {
        const remainingText = cleanedText.substring(currentPos);
        const maxSegmentLength = maxCharsPerLine * maxLines;
        
        // If remaining text is short enough for one segment
        if (remainingText.length <= maxSegmentLength) {
            const lines = splitIntoLines(remainingText, maxCharsPerLine, language);
            if (lines.length <= maxLines) {
                segments.push({
                    text: lines.join('\n'),
                    lines: lines,
                    startPos: currentPos,
                    endPos: cleanedText.length
                });
                break;
            }
        }
        
        // Find the best break point for this segment
        const targetBreak = currentPos + maxSegmentLength;
        const breakPoint = findBreakPoint(cleanedText, targetBreak, language, cleanedText.length);
        
        // Extract the segment text
        let segmentText = cleanedText.substring(currentPos, breakPoint).trim();
        
        // Split segment into lines respecting line length limits
        const lines = splitIntoLines(segmentText, maxCharsPerLine, language);
        
        // If we have too many lines, we need to trim the segment
        if (lines.length > maxLines) {
            // Take only the allowed number of lines and find where that ends
            const allowedLines = lines.slice(0, maxLines);
            const allowedText = allowedLines.join('\n');
            const adjustedBreakPoint = currentPos + allowedText.length;
            
            // Find a better break point for the reduced text
            const betterBreak = findBreakPoint(cleanedText, adjustedBreakPoint, language, breakPoint);
            segmentText = cleanedText.substring(currentPos, betterBreak).trim();
            
            // Re-split with the adjusted text
            const finalLines = splitIntoLines(segmentText, maxCharsPerLine, language);
            
            segments.push({
                text: finalLines.join('\n'),
                lines: finalLines,
                startPos: currentPos,
                endPos: betterBreak
            });
            
            currentPos = betterBreak;
        } else {
            segments.push({
                text: lines.join('\n'),
                lines: lines,
                startPos: currentPos,
                endPos: breakPoint
            });
            
            currentPos = breakPoint;
        }
        
        // Skip any leading whitespace for next segment
        while (currentPos < cleanedText.length && /\s/.test(cleanedText[currentPos])) {
            currentPos++;
        }
    }
    
    return segments.filter(segment => segment.text.trim().length > 0);
}

/**
 * Split text into lines respecting character limits
 * @param {string} text - Text to split
 * @param {number} maxCharsPerLine - Maximum characters per line
 * @param {string} language - Language for boundary detection
 * @returns {Array} Array of lines
 */
function splitIntoLines(text, maxCharsPerLine, language) {
    const words = splitIntoWords(text, language);
    const lines = [];
    let currentLine = '';
    
    for (const word of words) {
        const testLine = currentLine ? currentLine + ' ' + word : word;
        
        if (testLine.length <= maxCharsPerLine) {
            currentLine = testLine;
        } else {
            // If current word alone is too long, we need to break it
            if (word.length > maxCharsPerLine) {
                // If we have something in current line, push it first
                if (currentLine) {
                    lines.push(currentLine);
                    currentLine = '';
                }
                
                // Break the long word
                const brokenParts = breakLongWord(word, maxCharsPerLine, language);
                for (let i = 0; i < brokenParts.length - 1; i++) {
                    lines.push(brokenParts[i]);
                }
                currentLine = brokenParts[brokenParts.length - 1];
            } else {
                // Push current line and start new line with current word
                if (currentLine) {
                    lines.push(currentLine);
                }
                currentLine = word;
            }
        }
    }
    
    if (currentLine) {
        lines.push(currentLine);
    }
    
    return lines;
}

/**
 * Split text into words based on language
 * @param {string} text - Text to split
 * @param {string} language - Language for word boundary detection
 * @returns {Array} Array of words
 */
function splitIntoWords(text, language) {
    const wordBoundaries = getWordBoundaries(language);
    
    // For languages that don't use spaces (like Chinese, Japanese)
    if (language === 'Chinese' || language === 'Japanese') {
        // For now, treat each character as a potential break point
        // In a real implementation, you might want to use a proper segmenter
        return text.split('').filter(char => char.trim());
    }
    
    // For space-separated languages
    return text.split(/\s+/).filter(word => word.length > 0);
}

/**
 * Break a word that's too long for a line
 * @param {string} word - Word to break
 * @param {number} maxChars - Maximum characters per line
 * @param {string} language - Language (for future language-specific breaking rules)
 * @returns {Array} Array of word parts
 */
function breakLongWord(word, maxChars, language) {
    const parts = [];
    let remaining = word;
    
    while (remaining.length > maxChars) {
        // Try to find a good break point (hyphen, etc.)
        const breakPos = findWordBreakPoint(remaining, maxChars - 1); // -1 for hyphen
        if (breakPos > 0 && breakPos < maxChars - 1) {
            parts.push(remaining.substring(0, breakPos) + '-');
            remaining = remaining.substring(breakPos);
        } else {
            // Just break at character limit
            parts.push(remaining.substring(0, maxChars - 1) + '-');
            remaining = remaining.substring(maxChars - 1);
        }
    }
    
    if (remaining) {
        parts.push(remaining);
    }
    
    return parts;
}

/**
 * Find a good break point within a word
 * @param {string} word - Word to find break point in
 * @param {number} maxPos - Maximum position to consider
 * @returns {number} Break position, or -1 if no good point found
 */
function findWordBreakPoint(word, maxPos) {
    // Look for existing hyphens or word boundary characters
    for (let i = Math.min(maxPos, word.length - 1); i > 0; i--) {
        const char = word[i];
        if (char === '-' || char === '–' || char === '—') {
            return i + 1;
        }
    }
    
    // No good break point found
    return -1;
}

/**
 * Convert segmented transcript to subtitle format
 * @param {Array} segments - Array of text segments
 * @param {number} startTime - Starting time in seconds
 * @param {number} totalDuration - Total duration for all segments in seconds
 * @returns {Array} Array of subtitle objects
 */
export function segmentsToSubtitles(segments, startTime = 0, totalDuration = null) {
    if (!segments || segments.length === 0) return [];
    
    const subtitles = [];
    
    // Calculate duration per segment
    let timePerSegment;
    if (totalDuration && totalDuration > 0) {
        // Use actual video duration - distribute segments evenly across the entire video
        timePerSegment = totalDuration / segments.length;
        console.log(`Using video duration: ${totalDuration}s, ${segments.length} segments, ${timePerSegment.toFixed(2)}s per segment`);
    } else {
        // Default to 3 seconds per segment if no duration provided
        timePerSegment = 3;
        console.log(`Using default timing: ${timePerSegment}s per segment`);
    }
    
    segments.forEach((segment, index) => {
        const segmentStart = startTime + (index * timePerSegment);
        const segmentEnd = startTime + ((index + 1) * timePerSegment);
        
        // Ensure we don't exceed the total duration
        const finalEnd = totalDuration && totalDuration > 0 ? Math.min(segmentEnd, totalDuration) : segmentEnd;
        
        subtitles.push({
            start: formatTime(segmentStart),
            end: formatTime(finalEnd),
            text: segment.text,
            text2: segment.text, // For dual language support
            note: '',
            bookmark: false,
            duration: finalEnd - segmentStart,
            startTime: segmentStart,
            endTime: finalEnd
        });
    });
    
    return subtitles;
}

/**
 * Format time in HH:MM:SS.mmm format
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string
 */
function formatTime(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
}

/**
 * Process a transcript file and convert to subtitle segments
 * @param {string} transcriptText - Raw transcript text
 * @param {string} language - Language of the transcript
 * @param {Object} options - Options for segmentation
 * @returns {Object} Result object with segments and subtitles
 */
export function processTranscript(transcriptText, language = 'English', options = {}) {
    const {
        maxCharsPerLine = 35,
        maxLines = 2,
        startTime = 0,
        estimatedDuration = null
    } = options;
    
    try {
        // Segment the transcript
        const segments = segmentTranscript(transcriptText, language, maxCharsPerLine, maxLines);
        
        if (segments.length === 0) {
            return { success: false, error: 'No segments created from transcript', segments: [], subtitles: [] };
        }
        
        // Calculate duration for subtitle timing
        let totalDuration;
        if (estimatedDuration && estimatedDuration > 0) {
            // Use the actual video duration
            totalDuration = estimatedDuration;
            console.log(`Using video duration: ${totalDuration} seconds for ${segments.length} segments`);
        } else {
            // Default to 3 seconds per segment if no video duration available
            totalDuration = segments.length * 3;
            console.log(`Using default duration: ${totalDuration} seconds for ${segments.length} segments`);
        }
        
        // Convert to subtitles with proper timing
        const subtitles = segmentsToSubtitles(segments, startTime, totalDuration);
        
        return {
            success: true,
            segments,
            subtitles,
            totalSegments: segments.length,
            totalDuration: totalDuration,
            videoDuration: estimatedDuration, // Store the original video duration
            language
        };
    } catch (error) {
        console.error('Error processing transcript:', error);
        return { 
            success: false, 
            error: error.message, 
            segments: [], 
            subtitles: [] 
        };
    }
}

export default {
    segmentTranscript,
    segmentsToSubtitles,
    processTranscript
};